import { Card } from "~/features/configurator/Linear/components/Card";
import { Button, Step, StepLabel, Stepper, styled, Typography } from "~/common/mui";
import { type CSSProperties, useState } from "react";
import { Step1 } from "~/features/registration/components/CustomerRegistrationPage/components/Step1";
import { Step2 } from "~/features/registration/components/CustomerRegistrationPage/components/Step2";
import { Step3 } from "~/features/registration/components/CustomerRegistrationPage/components/Step3";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { Container } from "~/common/components/Layout";
import { C_OUTLINE_VARIANT } from "~/common/components/icon/Colors";
import { z } from "zod";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { zRequiredEmail, zRequiredNumber, zRequiredString } from "~/common/utils/schemaUtils";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useTranslation } from "react-i18next";
import {
  apiSubmitCustomerRegistration,
  type SubmitCustomerRegistrationDTO,
} from "~/features/registration/components/CustomerRegistrationPage/api/submitCustomerRegistration";
import { Alert } from "~/common/mui/Alert/Alert";
import { Success } from "~/features/registration/components/CustomerRegistrationPage/components/Success";

interface Props {
  className?: string;
  countries: string[];
  currencies: string[];
  sectors: Record<string, string>;
  jobTitles: Record<string, string>;
}

export const CustomerRegistrationPage = (props: Props) => {
  const { t } = useTranslation();
  const { prevButtonProps, nextButtonProps, submitButtonProps, step } = useSteps();

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const screenSize = useNumericScreenSize();
  const titleVariant = screenSize < useNumericScreenSize.tablet ? "bodyLarge" : "headlineMedium";

  const formProps = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(formSchema),
    shouldFocusError: true,
  });

  const { handleSubmit, formState } = formProps;

  const submit = handleSubmit(async (data) => {
    setError(false);
    try {
      const response = await apiSubmitCustomerRegistration(transform(data as CustomerRegistrationFormData));
      if (response.ok) {
        setSubmitted(true);
      } else {
        setError(true);
        console.error("Error!");
      }
    } catch (error) {
      setError(true);
      console.error(error);
    }
  });

  return (
    <FormProvider {...formProps}>
      {!submitted && (
        <StyledCard className={props.className}>
          {error && (
            <Alert severity={"error"} sx={{ width: "100%" }}>
              {t("generic.error.ajax")}
            </Alert>
          )}
          {submitted && (
            <Alert severity={"success"} sx={{ width: "100%" }}>
              Success
            </Alert>
          )}
          <Typography variant={titleVariant} sx={{ textAlign: { phone: "left", tablet: "center" } }}>
            {t("feature.selfRegister.form.title")}
          </Typography>
          <FormStepper activeStep={step} steps={STEPS} />
          {step === 0 && <Step1 currencies={props.currencies} countries={props.countries} sectors={props.sectors} />}
          {step === 1 && <Step2 jobTitles={props.jobTitles} />}
          {step === 2 && <Step3 sectors={props.sectors} jobTitles={props.jobTitles} />}
          <MobileStickyButtonContainer>
            <ButtonContainer>
              <Button
                variant={"text"}
                fullWidth
                {...prevButtonProps}
                startIcon={<ChevronLeftIcon />}
                onClick={async () => {
                  if (await formProps.trigger(FIELDS_BY_STEP[step - 1], { shouldFocus: true })) {
                    prevButtonProps.onClick();
                  }
                }}
                disabled={formState.isSubmitting}
              >
                {t("generic.back")}
              </Button>
              <Button
                fullWidth
                {...nextButtonProps}
                onClick={async () => {
                  if (await formProps.trigger(FIELDS_BY_STEP[step], { shouldFocus: true })) {
                    nextButtonProps.onClick();
                  }
                }}
                disabled={formState.isSubmitting}
              >
                {t("generic.next")}
              </Button>
              <Button fullWidth {...submitButtonProps} onClick={submit} disabled={formState.isSubmitting}>
                {t("generic.submit")}
              </Button>
            </ButtonContainer>
          </MobileStickyButtonContainer>
        </StyledCard>
      )}
      {submitted && <Success />}
    </FormProvider>
  );
};

const StyledCard = styled(Card)(({ theme }) => ({
  [theme.breakpoints.down("desktop-s")]: {
    ["&:after"]: {
      content: "unset",
    },
  },

  ["input[type=number]"]: {
    MozAppearance: "textfield",
  },
  "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },

  display: "flex",
  flexFlow: "column",
  alignItems: "stretch",
  gap: "16px",
  [theme.breakpoints.up("tablet")]: {
    alignItems: "center",
  },

  [theme.breakpoints.up("desktop-s")]: {
    marginInline: "auto",
    width: "700px",
    padding: "24px",

    gap: "32px",
  },
}));

const STEPS = [
  {
    label: "page.selfRegistration.step.1.label",
  },
  {
    label: "page.selfRegistration.step.2.label",
  },
  {
    label: "page.selfRegistration.step.3.label",
  },
];
type StepType = {
  label: string;
};

const FormStepper = styled(
  ({ activeStep, steps, className }: { activeStep: number; steps: StepType[]; className?: string }) => {
    const screenSize = useNumericScreenSize();
    const { t } = useTranslation();

    const isPhone = screenSize <= useNumericScreenSize.phone;

    return (
      <div className={className}>
        {isPhone && <Typography variant={"bodyLarge"}>{t(steps[activeStep].label)}</Typography>}
        <Stepper activeStep={activeStep} sx={{ width: "100%" }}>
          {steps.map((step, index) => (
            <Step key={index}>
              <StepLabel>{!isPhone && t(step.label)}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </div>
    );
  },
)(({ theme }) => ({
  ...stackerStyles(8),
  borderBottom: `1px solid ${C_OUTLINE_VARIANT}`,
  paddingBlock: "8px",
  width: "100vw",
  alignSelf: "center",
  paddingInline: "var(--container-padding, 0)",

  [theme.breakpoints.up("tablet")]: {
    display: "contents",
  },
  [theme.breakpoints.down("tablet")]: {
    order: -1,
    marginTop: -32,
    ["& .MuiStepper-root"]: {
      marginInline: -8,
    },
    ["& .MuiStepLabel-iconContainer"]: {
      paddingRight: 0,
    },
  },
}));

const stackerStyles = (gap: number) =>
  ({
    display: "flex",
    flexDirection: "column",
    gap: `${gap}px`,
  }) as const;

const ButtonContainer = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  gap: "24px",
  "> *": {
    flex: 1,
  },
}));

const MobileStickyButtonContainer = styled(Container)(({ theme }) => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  width: "100vw",
  paddingBlock: "8px",
  background: "white",
  borderTop: `1px solid ${C_OUTLINE_VARIANT}`,
  zIndex: 1,
  [theme.breakpoints.up("desktop-s")]: {
    display: "contents",
  },
}));

const useSteps = () => {
  const [step, setStep] = useState(0);

  return {
    prevButtonProps: {
      style: step === 0 ? ({ visibility: "hidden" } as CSSProperties) : undefined,
      onClick: () => setStep(step - 1),
    },
    nextButtonProps: {
      style: step === 2 ? ({ display: "none" } as CSSProperties) : undefined,
      onClick: () => setStep(step + 1),
    },
    submitButtonProps: {
      style: step !== 2 ? ({ display: "none" } as CSSProperties) : undefined,
    },
    step,
  };
};

export const STEP_1_FIELDS = [
  "companyName",
  "street",
  "zipCode",
  "city",
  "country",
  "sector",
  "volume",
  "currency",
  "suppliers",
  "reasons",
  "taxId",
] as const;

export const STEP_2_FIELDS = ["fullName", "jobTitle", "email", "telephone", "marketingConsent"] as const;

const FIELDS_BY_STEP = [STEP_1_FIELDS, STEP_2_FIELDS, [...STEP_1_FIELDS, ...STEP_2_FIELDS]] as const;

const ERROR_REQUIRED = "generic.field.required";
const ERROR_TOO_BIG = "generic.field.tooBig";
const ERROR_NUMBER = "generic.field.number";
const ERROR_EMAIL = "profile.email.invalid";

const formSchema = z.object({
  companyName: zRequiredString(ERROR_REQUIRED),
  street: zRequiredString(ERROR_REQUIRED),
  zipCode: zRequiredString(ERROR_REQUIRED),
  city: zRequiredString(ERROR_REQUIRED),
  country: zRequiredString(ERROR_REQUIRED),
  sector: zRequiredString(ERROR_REQUIRED),
  currency: zRequiredString(ERROR_REQUIRED),
  taxId: z.string().optional(),
  volume: zRequiredNumber(ERROR_REQUIRED, ERROR_NUMBER),
  suppliers: zRequiredString(ERROR_REQUIRED).max(250, ERROR_TOO_BIG),
  reasons: zRequiredString(ERROR_REQUIRED).max(250, ERROR_TOO_BIG),
  fullName: zRequiredString(ERROR_REQUIRED),
  jobTitle: zRequiredString(ERROR_REQUIRED),
  email: zRequiredEmail(ERROR_REQUIRED, ERROR_EMAIL),
  telephone: z.string().optional(),
  marketingConsent: z.boolean().optional(),
});

export type CustomerRegistrationFormData = z.infer<typeof formSchema>;

function transform(data: CustomerRegistrationFormData): SubmitCustomerRegistrationDTO {
  return {
    companyName: data.companyName,
    addressLine1: data.street,
    postalCode: data.zipCode,
    city: data.city,
    country: data.country,
    purchaseAmt: data.volume,
    currency: data.currency,
    sector: data.sector,
    taxId: data.taxId,
    fullName: data.fullName,
    jobTitle: data.jobTitle,
    email: data.email,
    telephoneNumber: data.telephone,
    whySchaeffler: data.reasons,
    yourCurrentSupplier: data.suppliers,
    consent: data.marketingConsent ?? false,
  };
}

const DEFAULT_VALUES = {
  companyName: "",
  street: "",
  zipCode: "",
  city: "",
  country: "",
  sector: "",
  currency: "",
  taxId: "",
  volume: null as number | null,
  suppliers: "",
  reasons: "",
  fullName: "",
  jobTitle: "",
  email: "",
  telephone: "",
  marketingConsent: false,
};
